import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {Password} from "primereact/password";
import {type ReactNode, useEffect, useRef, useState} from "react";

import TooltipButton from "../../component/TooltipButton";
import {useRevaTranslation} from "../../hooks/languageHooks";
import {useNewPasswordFields} from "../../hooks/useNewPasswordFields";
import {closeProfileDialog, selectProfileDialogOpen, selectUserProfile} from "../../redux/accountManagementSlice";
import {
    fetchUserProfileThunk,
    updateUserPasswordThunk,
    updateUserProfileThunk,
    verifyCurrentPasswordThunk,
} from "../../redux/accountManagementThunk";
import {selectEmailVerificationHint, selectUsername} from "../../redux/authSlice";
import {requestEmailVerificationThunk} from "../../redux/emailVerificationThunk";
import {useRevaDispatch, useRevaSelector} from "../../redux/revaHooks";
import {isValidEmail} from "../../util/emailUtil";
import {resolveUserApiErrorMessage} from "../../util/errorUtil";
import {type CharGroup, checkPassword, PASSWORD_MIN_LENGTH} from "../../util/passwordRules";
import {
    getPendingEmailChange,
    isMailPending,
    markMailSent,
    markPendingEmailChange,
    type SendState,
} from "./EmailVerificationNotice";
import GeneratedPasswordDisplay from "./GeneratedPasswordDisplay";

/**
 * #19351: Dialog to edit the logged-in user's name, e-mail and password, replacing the links to
 * accounts.immobrain.net. Opened via the header user menu ({@link HeaderUserMenu}, the sole "Profil
 * bearbeiten" entry) or the e-mail-verification notice ({@link EmailVerificationNotice}); both merely
 * dispatch `openProfileDialog`. The UI language is deliberately NOT edited here — that stays the
 * language switcher's job (bottom right), which saves via the same `updateUserProfileThunk`.
 *
 * The name is edited inline. E-mail and password are collapsed rows with a "change" button; opening one shows two
 * steps: (1) the current password, checked right away via POST /user/password/verify, then (2) the new value with a
 * live hint what is still missing. Only one of the two is open at a time; a confirmed current password stays
 * confirmed until the dialog closes. The save calls send it again, since account-management re-checks it.
 *
 * Every successful action closes the dialog and shows a small confirmation dialog instead, so the footer only
 * offers "Abbrechen": while the dialog is open, nothing has been saved yet. A generated password therefore has to
 * be copied or marked as noted before it can be set.
 *
 * Without a verified address the password row is hidden (reva-rest answers 422 as well): there would be no way
 * back via "Passwort vergessen". That includes accounts without any address. The hint below the address says why and
 * offers the verification link resp. points to "E-Mail hinterlegen". With a verified address a mistyped new password
 * is recoverable, so there is no repeat field.
 */
const MIN_FULL_NAME = 3;
/** Same rule as account-management (ProfileResource.FULL_NAME): letters, space, period, comma, hyphen, apostrophe, parentheses. */
const FULL_NAME = /^(?!.*[wW]{3}\.)\p{L}[\p{L}\p{M} .,'’ʼ()-]*$/u;

/** Same normalization as account-management (ProfileResource.normalizeFullName). */
function normalizeFullName(name: string): string {
    return name.normalize("NFC").replace(/\s+/g, " ").trim();
}

/** Text of the first check that holds: why a save button is still disabled (shown as its tooltip). */
function firstBlocker(...checks: [boolean, string][]): string | undefined {
    return checks.find(([blocked]) => blocked)?.[1];
}

type Section = "email" | "password";
type Tone = "neutral" | "hint" | "bad" | "ok";

interface Confirmation {
    title: string;
    text: string;
}

const TONE_TEXT: Record<Tone, string> = {
    neutral: "text-surface-500",
    hint: "text-orange-600",
    bad: "text-red-600",
    ok: "text-green-600",
};

const TONE_INPUT: Record<Tone, string> = {
    neutral: "",
    hint: "",
    bad: " p-invalid",
    ok: " !border-green-500",
};

const GROUP_KEYS: Record<CharGroup, string> = {
    upper: "header.profileDialog.groupUpper",
    lower: "header.profileDialog.groupLower",
    digit: "header.profileDialog.groupDigit",
    symbol: "header.profileDialog.groupSymbol",
};

function StepNumber({n, done = false}: {n: number; done?: boolean}) {
    return (
        <span
            aria-hidden
            className={`inline-grid place-items-center w-5 h-5 rounded-full text-xs font-bold shrink-0 ${
                done ? "bg-green-100 text-green-700" : "bg-primary-100 text-primary-700"
            }`}
        >
            {done ? <i className="pi pi-check text-[0.65rem]" /> : n}
        </span>
    );
}

export default function ProfileDialog() {
    const {t, language} = useRevaTranslation();
    const dispatch = useRevaDispatch();

    const visible = useRevaSelector(selectProfileDialogOpen);
    const profile = useRevaSelector(selectUserProfile);
    const username = useRevaSelector(selectUsername);
    const verificationEnabled = useRevaSelector(selectEmailVerificationHint);

    const [loaded, setLoaded] = useState(false);
    const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
    const [openSection, setOpenSection] = useState<Section | null>(null);

    const [fullName, setFullName] = useState("");
    const [savingName, setSavingName] = useState(false);
    const [errorName, setErrorName] = useState<string | null>(null);

    // Step 1, shared by the e-mail and the password change.
    const [currentPassword, setCurrentPassword] = useState("");
    const [currentConfirmed, setCurrentConfirmed] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [verifyError, setVerifyError] = useState<string | null>(null);
    const currentPasswordRef = useRef<HTMLInputElement>(null);
    const newValueRef = useRef<HTMLInputElement>(null);

    const [newEmail, setNewEmail] = useState("");
    const [savingEmail, setSavingEmail] = useState(false);
    const [errorEmail, setErrorEmail] = useState<string | null>(null);
    const [sendState, setSendState] = useState<SendState>("idle");
    const [linkPending, setLinkPending] = useState(false);
    const [pendingEmail, setPendingEmail] = useState<string | null>(null);

    const newPw = useNewPasswordFields(false);
    const {newPassword} = newPw;
    const resetNewPassword = newPw.reset;
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [generatedNoted, setGeneratedNoted] = useState(false);
    const [savingPassword, setSavingPassword] = useState(false);
    const [errorPassword, setErrorPassword] = useState<string | null>(null);

    // Reset on open and on close, so no password outlives the dialog.
    useEffect(() => {
        setOpenSection(null);
        setCurrentPassword("");
        setCurrentConfirmed(false);
        setVerifyError(null);
        setNewEmail("");
        setErrorEmail(null);
        resetNewPassword();
        setShowNewPassword(false);
        setGeneratedNoted(false);
        setErrorPassword(null);
        setErrorName(null);
        if (!visible) return;
        setLoaded(false);
        setSendState("idle");
        setLinkPending(isMailPending(username));
        setPendingEmail(getPendingEmailChange(username));
        void dispatch(fetchUserProfileThunk()).then(() => setLoaded(true));
    }, [visible, dispatch, username, resetNewPassword]);

    useEffect(() => {
        if (visible && profile) {
            setFullName(profile.fullName ?? "");
        }
    }, [visible, profile]);

    // Focus follows the steps: the current password first, then the new value.
    useEffect(() => {
        if (!openSection) return;
        const target = currentConfirmed ? newValueRef : currentPasswordRef;
        const timer = setTimeout(() => target.current?.focus(), 0);
        return () => clearTimeout(timer);
    }, [openSection, currentConfirmed]);

    const editable = loaded && (profile?.editable ?? false);
    const nameDirty = fullName !== (profile?.fullName ?? "");

    /** Same minimum as account-management (ProfileResource.MIN_FULL_NAME). */
    const normalizedName = normalizeFullName(fullName);
    const nameTooShort = nameDirty && normalizedName.length < MIN_FULL_NAME;
    // A legacy name set by an admin (e.g. with parentheses) stays valid as long as it is left unchanged.
    const nameBadChars = nameDirty && normalizedName !== (profile?.fullName ?? "") && !FULL_NAME.test(normalizedName);
    const nameInvalid = nameTooShort || nameBadChars;
    const nameBlocker = firstBlocker(
        [nameTooShort, t("header.profileDialog.nameTooShort", {min: MIN_FULL_NAME})],
        [nameBadChars, t("header.profileDialog.nameInvalidChars")],
    );

    const onClose = () => dispatch(closeProfileDialog());

    /** A successful action closes the dialog and confirms itself in a small dialog of its own. */
    const finish = (title: string, text: string) => {
        setConfirmation({title, text});
        dispatch(closeProfileDialog());
    };

    const resolveError = (e: unknown, unavailableKey: string) =>
        resolveUserApiErrorMessage(e, language, t(unavailableKey), t("general.error.unspecific"));

    const clearSectionInputs = () => {
        setNewEmail("");
        setErrorEmail(null);
        resetNewPassword();
        setShowNewPassword(false);
        setGeneratedNoted(false);
        setErrorPassword(null);
    };

    const openSectionFor = (section: Section) => {
        clearSectionInputs();
        setVerifyError(null);
        setOpenSection(section);
    };

    const cancelSection = () => {
        clearSectionInputs();
        setOpenSection(null);
    };

    const onVerify = async () => {
        if (!currentPassword || verifying) return;
        setVerifying(true);
        setVerifyError(null);
        try {
            await dispatch(verifyCurrentPasswordThunk(currentPassword));
            setCurrentConfirmed(true);
        } catch (e) {
            setVerifyError(resolveError(e, "header.profileDialog.unavailable"));
            currentPasswordRef.current?.select();
        } finally {
            setVerifying(false);
        }
    };

    const onReenterCurrentPassword = () => {
        setCurrentConfirmed(false);
        setCurrentPassword("");
        setVerifyError(null);
    };

    const onSaveName = async () => {
        setSavingName(true);
        setErrorName(null);
        try {
            const response = await dispatch(updateUserProfileThunk({fullName}));
            finish(
                t("header.profileDialog.nameSavedTitle"),
                t("header.profileDialog.nameSavedText", {name: response.profile.fullName ?? normalizedName}),
            );
        } catch (e) {
            setErrorName(resolveError(e, "header.profileDialog.unavailable"));
        } finally {
            setSavingName(false);
        }
    };

    const trimmedNewEmail = newEmail.trim();
    const newEmailState: "missing" | "invalid" | "same" | "ok" = !trimmedNewEmail
        ? "missing"
        : !isValidEmail(trimmedNewEmail)
          ? "invalid"
          : trimmedNewEmail.toLowerCase() === (profile?.email ?? "").toLowerCase()
            ? "same"
            : "ok";
    const newEmailFeedback: Record<typeof newEmailState, [string, Tone]> = {
        missing: [t("header.profileDialog.newEmailMissing"), "neutral"],
        invalid: [t("header.profileDialog.emailInvalid"), "hint"],
        same: [t("header.profileDialog.newEmailSame"), "hint"],
        ok: [t("header.profileDialog.newEmailOk"), "ok"],
    };
    const [newEmailText, newEmailTone] = newEmailFeedback[newEmailState];
    const canSaveEmail = editable && currentConfirmed && newEmailState === "ok" && !savingEmail;

    const onSaveEmail = async () => {
        if (!canSaveEmail) return;
        setSavingEmail(true);
        setErrorEmail(null);
        try {
            const response = await dispatch(updateUserProfileThunk({email: trimmedNewEmail, currentPassword}));
            if (response.pendingEmail) {
                // A verified address is only replaced once the link to the new one is redeemed (#19351).
                markPendingEmailChange(username, response.pendingEmail);
                finish(
                    t("header.profileDialog.emailChangeRequestedTitle"),
                    t("header.profileDialog.emailChangeRequested", {
                        email: response.pendingEmail,
                        current: response.profile.email,
                    }),
                );
            } else if (response.verificationMailSent) {
                markMailSent(username);
                finish(
                    t("header.profileDialog.verificationMailSentTitle"),
                    t("header.profileDialog.verificationMailSent", {email: response.profile.email}),
                );
            } else {
                finish(
                    t("header.profileDialog.emailSavedTitle"),
                    t("header.profileDialog.emailSavedText", {email: response.profile.email}),
                );
            }
        } catch (e) {
            setErrorEmail(resolveError(e, "header.profileDialog.unavailable"));
        } finally {
            setSavingEmail(false);
        }
    };

    const onSendVerificationLink = async () => {
        setSendState("sending");
        const status = await dispatch(requestEmailVerificationThunk());
        // THROTTLED means a mail went out a moment ago - that link is just as usable.
        if (status === "SENT" || status === "THROTTLED") {
            markMailSent(username);
            setSendState(status);
            finish(
                t("header.profileDialog.linkSentTitle"),
                t("header.profileDialog.linkSentText", {email: profile?.email}),
            );
            return;
        }
        if (status === "ALREADY_VERIFIED") {
            void dispatch(fetchUserProfileThunk());
        }
        setSendState(status);
    };

    const sendFeedback: Partial<Record<SendState, string>> = {
        NOT_ELIGIBLE: t("header.emailHint.notEligible"),
        ERROR: t("header.emailHint.error"),
    };

    const showEmailStatus = !!profile?.email;
    const showPendingChange = !!pendingEmail && pendingEmail !== profile?.email;
    const emailVerified = profile?.emailVerified ?? false;
    // Without a verified address (none at all or an unverified one) there is no password change; reva-rest answers 422.
    const passwordLocked = !emailVerified;

    const personalData = {username, fullName: profile?.fullName, email: profile?.email};
    const passwordCheck = checkPassword(newPassword, personalData);
    const passwordFeedback = (): [string, Tone] => {
        if (!newPassword) return [t("header.profileDialog.rulesIntro", {min: PASSWORD_MIN_LENGTH}), "neutral"];
        if (!passwordCheck.lengthOk)
            return [t("header.profileDialog.tooShort", {count: passwordCheck.missingLength}), "hint"];
        if (!passwordCheck.groupsOk) {
            const names = passwordCheck.missingGroups.map((group) => t(GROUP_KEYS[group]));
            if (passwordCheck.neededGroups === 1) {
                const joined =
                    names.length > 1
                        ? `${names.slice(0, -1).join(", ")} ${t("header.profileDialog.or")} ${names[names.length - 1]}`
                        : names[0];
                return [t("header.profileDialog.needGroupsOne", {groups: joined}), "hint"];
            }
            return [
                t("header.profileDialog.needGroupsMany", {count: passwordCheck.neededGroups, groups: names.join(", ")}),
                "hint",
            ];
        }
        if (passwordCheck.personalPart)
            return [t("header.profileDialog.personalPart", {part: passwordCheck.personalPart}), "bad"];
        return [t("header.profileDialog.passwordOk"), "ok"];
    };
    const [passwordText, passwordTone] = passwordFeedback();
    const generatedNotKept = !!newPw.generatedPassword && !newPw.copied && !generatedNoted;
    const canSavePassword = editable && currentConfirmed && passwordCheck.ok && !generatedNotKept && !savingPassword;

    const onSavePassword = async () => {
        if (!canSavePassword) return;
        setSavingPassword(true);
        setErrorPassword(null);
        try {
            await dispatch(updateUserPasswordThunk({currentPassword, newPassword}));
            finish(t("header.profileDialog.passwordSuccessTitle"), t("header.profileDialog.passwordSuccess"));
        } catch (e) {
            setErrorPassword(resolveError(e, "header.profileDialog.unavailable"));
        } finally {
            setSavingPassword(false);
        }
    };

    const onGeneratePassword = () => {
        newPw.generatePassword((password) => checkPassword(password, personalData).ok);
        setShowNewPassword(true);
        setGeneratedNoted(false);
    };

    /** Step 1 of both changes; `prefix` keeps the e-mail's and the password's element ids apart. */
    const currentPasswordStep = (prefix: string, inputId: string, hintKey: string) =>
        currentConfirmed ? (
            <div id={`${prefix}CurrentPasswordConfirmed`} className="flex items-center gap-2 p-3">
                <StepNumber n={1} done />
                <span className="text-sm font-semibold text-green-700 dark:text-green-500">
                    {t("header.profileDialog.currentPasswordConfirmed")}
                </span>
                <Button
                    id={`${prefix}CurrentPasswordReenterBtn`}
                    type="button"
                    link
                    size="small"
                    className="!p-0 ml-auto"
                    label={t("header.profileDialog.reenter")}
                    onClick={onReenterCurrentPassword}
                />
            </div>
        ) : (
            <div className="flex flex-col gap-2 p-3">
                <label htmlFor={inputId} className="flex items-center gap-2 text-sm font-semibold">
                    <StepNumber n={1} />
                    {t("header.profileDialog.stepCurrentPassword")}
                </label>
                <div className="flex items-start gap-2">
                    <Password
                        inputId={inputId}
                        inputRef={currentPasswordRef}
                        inputClassName={`w-full p-inputtext-sm${verifyError ? " p-invalid" : ""}`}
                        className="w-full"
                        value={currentPassword}
                        disabled={!editable || verifying}
                        feedback={false}
                        toggleMask
                        autoComplete="current-password"
                        onChange={(e) => {
                            setCurrentPassword(e.target.value);
                            setVerifyError(null);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") void onVerify();
                        }}
                    />
                    <Button
                        id={`${prefix}VerifyBtn`}
                        type="button"
                        size="small"
                        className="shrink-0"
                        label={t(verifying ? "header.profileDialog.verifying" : "header.profileDialog.verify")}
                        loading={verifying}
                        disabled={!editable || !currentPassword || verifying}
                        onClick={() => void onVerify()}
                    />
                </div>
                <small id={`${prefix}VerifyFeedback`} className={verifyError ? "text-red-600" : "text-surface-500"}>
                    {verifyError ?? t(hintKey)}
                </small>
            </div>
        );

    const step2Title = (inputId: string, labelKey: string) => (
        <label
            htmlFor={inputId}
            className={`flex items-center gap-2 text-sm font-semibold${currentConfirmed ? "" : " text-surface-500"}`}
        >
            <StepNumber n={2} />
            {t(labelKey)}
        </label>
    );

    const stepper = (step1: ReactNode, step2: ReactNode) => (
        <div className="border border-surface-200 dark:border-surface-700 rounded-md">
            {step1}
            <div
                className={`flex flex-col gap-2 p-3 border-t border-surface-200 dark:border-surface-700${
                    currentConfirmed ? "" : " bg-surface-50 dark:bg-surface-800"
                }`}
            >
                {step2}
            </div>
        </div>
    );

    const sectionActions = (
        prefix: string,
        saveLabelKey: string,
        saving: boolean,
        canSave: boolean,
        reason: string,
        onSave: () => void,
    ) => (
        <div className="flex items-center justify-between gap-2 flex-wrap">
            <small id={`${prefix}Reason`} className="text-surface-500">
                {reason}
            </small>
            <div className="flex gap-2 ml-auto">
                <Button
                    id={`${prefix}CancelBtn`}
                    type="button"
                    text
                    size="small"
                    label={t("header.profileDialog.cancel")}
                    disabled={saving}
                    onClick={cancelSection}
                />
                <Button
                    id={`${prefix === "profileDialogEmail" ? "profileDialogSaveEmailBtn" : "profileDialogSavePasswordBtn"}`}
                    type="button"
                    size="small"
                    label={t(saveLabelKey)}
                    loading={saving}
                    disabled={!canSave}
                    onClick={onSave}
                />
            </div>
        </div>
    );

    // Only the rules still open are listed; once all are met, the green field border alone says so.
    const ruleOpen = (met: boolean) => !newPassword || !met;
    const rule = (id: string, failed: boolean, label: string) => (
        <li
            id={id}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs border ${
                failed
                    ? "border-transparent bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                    : "border-surface-300 text-surface-500"
            }`}
        >
            <i className={`pi ${failed ? "pi-times" : "pi-circle"} text-[0.6rem]`} aria-hidden />
            {label}
        </li>
    );

    const emailSection = (
        <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">{t("header.profileDialog.email")}</span>
                        {showEmailStatus && (
                            <span
                                id={emailVerified ? "profileDialogEmailVerified" : "profileDialogEmailUnverified"}
                                className={`flex items-center gap-1 text-xs ${emailVerified ? "text-green-600" : "text-orange-600"}`}
                                title={t(
                                    emailVerified
                                        ? "header.profileDialog.emailVerifiedHint"
                                        : "header.profileDialog.emailUnverifiedHint",
                                )}
                            >
                                <i
                                    className={`pi ${emailVerified ? "pi-check-circle" : "pi-exclamation-circle"}`}
                                    aria-hidden
                                />
                                {t(
                                    emailVerified
                                        ? "header.profileDialog.emailVerified"
                                        : "header.profileDialog.emailUnverified",
                                )}
                            </span>
                        )}
                    </div>
                    <span
                        id="profileDialogEmailValue"
                        className={`break-all${profile?.email ? "" : " text-surface-500"}`}
                    >
                        {profile?.email ?? t("header.profileDialog.noEmail")}
                    </span>
                </div>
                {openSection !== "email" && (
                    <Button
                        id="profileDialogChangeEmailBtn"
                        type="button"
                        size="small"
                        label={t(profile?.email ? "header.profileDialog.changeEmail" : "header.profileDialog.addEmail")}
                        disabled={!editable}
                        onClick={() => openSectionFor("email")}
                    />
                )}
            </div>
            {showPendingChange && openSection !== "email" && (
                <div id="profileDialogEmailChangePending" className="text-xs text-surface-500">
                    {t("header.profileDialog.emailChangePending", {email: pendingEmail})}
                </div>
            )}
            {profile && !emailVerified && openSection !== "email" && (
                <div
                    id="profileDialogEmailVerification"
                    className="flex flex-col items-start gap-2 rounded-md bg-orange-50 dark:bg-orange-900/20 p-3 text-sm"
                >
                    {!profile.email ? (
                        <span id="profileDialogEmailMissingHint">{t("header.profileDialog.emailMissingHint")}</span>
                    ) : linkPending ? (
                        <span id="profileDialogEmailLinkPending">{t("header.profileDialog.emailLinkPending")}</span>
                    ) : (
                        <>
                            <span>{t("header.profileDialog.emailUnverifiedHint")}</span>
                            {verificationEnabled && (
                                <Button
                                    id="profileDialogSendVerificationBtn"
                                    type="button"
                                    size="small"
                                    icon="pi pi-envelope"
                                    label={t("header.profileDialog.emailSendLink")}
                                    loading={sendState === "sending"}
                                    disabled={!editable || sendState === "sending"}
                                    onClick={() => void onSendVerificationLink()}
                                />
                            )}
                        </>
                    )}
                    {sendFeedback[sendState] && <span className="text-red-600">{sendFeedback[sendState]}</span>}
                </div>
            )}
            {openSection === "email" && (
                <>
                    {stepper(
                        currentPasswordStep(
                            "profileDialogEmail",
                            "profileDialogEmailCurrentPassword",
                            "header.profileDialog.emailCurrentPasswordHint",
                        ),
                        <>
                            {step2Title("profileDialogNewEmail", "header.profileDialog.stepNewEmail")}
                            <InputText
                                id="profileDialogNewEmail"
                                ref={newValueRef}
                                type="email"
                                autoComplete="email"
                                className={`w-full p-inputtext-sm${currentConfirmed && newEmail ? TONE_INPUT[newEmailTone] : ""}`}
                                value={newEmail}
                                placeholder={t(
                                    currentConfirmed
                                        ? "header.profileDialog.newEmailPlaceholder"
                                        : "header.profileDialog.lockedUntilStep1",
                                )}
                                disabled={!editable || !currentConfirmed || savingEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") void onSaveEmail();
                                }}
                            />
                            {currentConfirmed && (
                                <small id="profileDialogNewEmailFeedback" className={TONE_TEXT[newEmailTone]}>
                                    {newEmailText}
                                </small>
                            )}
                        </>,
                    )}
                    {sectionActions(
                        "profileDialogEmail",
                        "header.profileDialog.saveEmail",
                        savingEmail,
                        canSaveEmail,
                        currentConfirmed ? "" : t("header.profileDialog.currentPasswordFirst"),
                        () => void onSaveEmail(),
                    )}
                    {errorEmail && (
                        <Message id="profileDialogEmailError" severity="error" text={errorEmail} className="w-full" />
                    )}
                </>
            )}
        </div>
    );

    const passwordSection = passwordLocked ? null : (
        <div className="flex flex-col gap-2 border-t border-surface-200 dark:border-surface-700 pt-4">
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                    <span className="text-sm font-semibold">{t("header.profileDialog.passwordLabel")}</span>
                    <span aria-hidden className="tracking-[0.2em] text-surface-500">
                        ••••••••••
                    </span>
                </div>
                {openSection !== "password" && (
                    <Button
                        id="profileDialogChangePasswordBtn"
                        type="button"
                        size="small"
                        label={t("header.profileDialog.changePassword")}
                        disabled={!editable}
                        onClick={() => openSectionFor("password")}
                    />
                )}
            </div>
            {openSection === "password" && (
                <>
                    {stepper(
                        currentPasswordStep(
                            "profileDialogPassword",
                            "profileDialogCurrentPassword",
                            "header.profileDialog.currentPasswordHint",
                        ),
                        <>
                            {step2Title("profileDialogNewPassword", "header.profileDialog.newPassword")}
                            <div className="relative">
                                <InputText
                                    id="profileDialogNewPassword"
                                    ref={newValueRef}
                                    type={showNewPassword ? "text" : "password"}
                                    autoComplete="new-password"
                                    className={`w-full p-inputtext-sm !pr-20${currentConfirmed && newPassword ? TONE_INPUT[passwordTone] : ""}`}
                                    value={newPassword}
                                    placeholder={currentConfirmed ? "" : t("header.profileDialog.lockedUntilStep1")}
                                    aria-describedby="profileDialogNewPasswordFeedback"
                                    disabled={!editable || !currentConfirmed || savingPassword}
                                    onChange={(e) => newPw.changeNewPassword(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") void onSavePassword();
                                    }}
                                />
                                {currentConfirmed && !newPassword && (
                                    // A placeholder cannot hold the icon, so this overlay points to the lock button.
                                    <span
                                        id="profileDialogNewPasswordHint"
                                        aria-hidden
                                        className="pointer-events-none absolute left-3 right-20 top-1/2 -translate-y-1/2 truncate text-sm text-surface-400"
                                    >
                                        {t("header.profileDialog.newPasswordHint")} <i className="pi pi-lock text-xs" />{" "}
                                        →
                                    </span>
                                )}
                                <div className="absolute right-1 top-1/2 -translate-y-1/2 flex">
                                    <Button
                                        id="profileDialogNewPasswordToggle"
                                        type="button"
                                        text
                                        rounded
                                        size="small"
                                        className="!w-8 !h-8"
                                        icon={showNewPassword ? "pi pi-eye-slash" : "pi pi-eye"}
                                        aria-label={t("header.profileDialog.showPassword")}
                                        disabled={!currentConfirmed}
                                        onClick={() => setShowNewPassword((shown) => !shown)}
                                    />
                                    <Button
                                        id="profileDialogGenerateBtn"
                                        type="button"
                                        text
                                        rounded
                                        size="small"
                                        className="!w-8 !h-8"
                                        icon="pi pi-lock"
                                        aria-label={t("header.profileDialog.generate")}
                                        tooltip={t("header.profileDialog.generate")}
                                        tooltipOptions={{position: "top"}}
                                        disabled={!editable || !currentConfirmed || savingPassword}
                                        onClick={onGeneratePassword}
                                    />
                                </div>
                            </div>
                            {currentConfirmed && (
                                // "ok" stays for screen readers (aria-describedby); sighted users see the green border.
                                <small
                                    id="profileDialogNewPasswordFeedback"
                                    className={passwordTone === "ok" ? "sr-only" : TONE_TEXT[passwordTone]}
                                >
                                    {passwordText}
                                </small>
                            )}
                            {ruleOpen(passwordCheck.ok) && (
                                <ul
                                    id="profileDialogPasswordRules"
                                    className="flex flex-wrap gap-1.5 m-0 p-0 list-none"
                                >
                                    {ruleOpen(passwordCheck.lengthOk) &&
                                        rule(
                                            "profileDialogRuleLength",
                                            false,
                                            t("header.profileDialog.ruleLength", {min: PASSWORD_MIN_LENGTH}),
                                        )}
                                    {ruleOpen(passwordCheck.groupsOk) &&
                                        rule("profileDialogRuleGroups", false, t("header.profileDialog.ruleGroups"))}
                                    {ruleOpen(!passwordCheck.personalPart) &&
                                        rule(
                                            "profileDialogRulePersonal",
                                            !!passwordCheck.personalPart,
                                            t("header.profileDialog.rulePersonal"),
                                        )}
                                </ul>
                            )}
                            {newPw.generatedPassword && (
                                <GeneratedPasswordDisplay
                                    idPrefix="profileDialog"
                                    password={newPw.generatedPassword}
                                    copied={newPw.copied}
                                    onCopy={newPw.copyGeneratedPassword}
                                    hintKey="header.profileDialog.generatedHint"
                                    copyKey="header.profileDialog.copy"
                                    copiedKey="header.profileDialog.copied"
                                    onNoted={() => setGeneratedNoted(true)}
                                    noted={generatedNoted}
                                    notedKey="header.profileDialog.noted"
                                />
                            )}
                        </>,
                    )}
                    {sectionActions(
                        "profileDialogPassword",
                        "header.profileDialog.setPassword",
                        savingPassword,
                        canSavePassword,
                        !currentConfirmed
                            ? t("header.profileDialog.currentPasswordFirst")
                            : generatedNotKept
                              ? t("header.profileDialog.generatedNotNoted")
                              : "",
                        () => void onSavePassword(),
                    )}
                    {errorPassword && (
                        <Message
                            id="profileDialogPasswordError"
                            severity="error"
                            text={errorPassword}
                            className="w-full"
                        />
                    )}
                </>
            )}
        </div>
    );

    // While a section is open it has its own "Abbrechen"; a second one in the footer would only confuse.
    const footer = openSection ? undefined : (
        <div className="flex justify-end">
            <Button
                id="profileDialogCloseBtn"
                text
                size="small"
                label={t("header.profileDialog.cancel")}
                onClick={onClose}
            />
        </div>
    );

    return (
        <>
            <Dialog
                id="profileDialog"
                header={<span className="font-semibold">{t("header.profileDialog.header")}</span>}
                visible={visible}
                onHide={onClose}
                style={{width: "38rem"}}
                footer={footer}
            >
                {!loaded && (
                    <div className="text-center py-4">
                        <i className="pi pi-spin pi-spinner" style={{fontSize: "1.5rem"}} />
                    </div>
                )}
                {loaded && !editable && (
                    <Message
                        id="profileDialogUnavailable"
                        severity="warn"
                        text={t("header.userMenu.accountManagementUnavailable")}
                        className="w-full mb-3"
                    />
                )}
                {loaded && (
                    <div className="flex flex-col gap-5">
                        {/* Section 1: full name */}
                        <div className="flex flex-col gap-1">
                            <label htmlFor="profileDialogFullName" className="block text-sm font-semibold">
                                {t("header.profileDialog.fullName")}
                            </label>
                            <div className="flex items-center gap-2">
                                <InputText
                                    id="profileDialogFullName"
                                    className={`w-full p-inputtext-sm${nameInvalid ? " p-invalid" : ""}`}
                                    value={fullName}
                                    disabled={!editable || savingName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && nameDirty && !nameBlocker && !savingName)
                                            void onSaveName();
                                    }}
                                />
                                <TooltipButton
                                    id="profileDialogSaveNameBtn"
                                    wrapperClassName="shrink-0"
                                    size="small"
                                    label={t("header.profileDialog.save")}
                                    loading={savingName}
                                    disabled={!editable || savingName || !nameDirty || !!nameBlocker}
                                    tooltip={editable ? nameBlocker : undefined}
                                    onClick={() => void onSaveName()}
                                />
                            </div>
                            {errorName && (
                                <Message
                                    id="profileDialogNameError"
                                    severity="error"
                                    text={errorName}
                                    className="w-full"
                                />
                            )}
                        </div>

                        {/* Section 2: e-mail address */}
                        {emailSection}

                        {/* Section 3: password */}
                        {passwordSection}
                    </div>
                )}
            </Dialog>
            <Dialog
                id="profileDialogConfirmation"
                header={
                    <span id="profileDialogConfirmationTitle" className="flex items-center gap-2 font-semibold">
                        <i className="pi pi-check-circle text-green-600" aria-hidden />
                        {confirmation?.title}
                    </span>
                }
                visible={!!confirmation}
                onHide={() => setConfirmation(null)}
                style={{width: "30rem"}}
                footer={
                    <div className="flex justify-end">
                        <Button
                            id="profileDialogConfirmationCloseBtn"
                            text
                            size="small"
                            autoFocus
                            label={t("header.profileDialog.close")}
                            onClick={() => setConfirmation(null)}
                        />
                    </div>
                }
            >
                <p id="profileDialogConfirmationText" className="m-0">
                    {confirmation?.text}
                </p>
            </Dialog>
        </>
    );
}
